<template>
  <div>
    <Message v-if="!hasProjectContext && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="fatalError" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="loading || !userProjectContextReady"
      class="flex items-center justify-center py-16 text-[var(--color-disabled)]"
    >
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.overview.loading') }}
    </div>

    <div
      v-else-if="isEmpty"
      class="overview-page__empty p-8 text-center text-[var(--color-disabled)]"
    >
      <i class="pi pi-briefcase text-3xl mb-3 block" />
      <div class="font-medium text-[var(--color-dark)]">
        {{ $t('UserProjects.overview.emptyTitle') }}
      </div>
      <div class="text-sm mt-1">{{ $t('UserProjects.overview.emptyHint') }}</div>
    </div>

    <template v-else-if="hasProjectContext">
      <OverviewHeroHeader />
      <OverviewKpiStrip />
      <OverviewDecisionAlert @open-decision-log="openDecisionLog" />
      <OverviewMilestoneTimeline @select="openMilestoneDrawer" />
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6 items-start">
        <div class="lg:col-span-3">
          <OverviewMyCommitmentsPanel @refresh="refresh" />
        </div>
        <div class="lg:col-span-2">
          <OverviewConstraintsPanel />
        </div>
      </div>
      <OverviewPpcTrendChart />
    </template>

    <OverviewDecisionLogDialog
      v-model:visible="decisionLogVisible"
      :open-create-on-show="openCreateOnShow"
    />

    <OverviewMilestoneDrawer
      v-model:visible="milestoneDrawerVisible"
      :milestone="selectedMilestone"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Message from 'primevue/message';
import OverviewHeroHeader from '@/components/CustomerApp/UserProjects/OverviewHeroHeader.vue';
import OverviewKpiStrip from '@/components/CustomerApp/UserProjects/OverviewKpiStrip.vue';
import OverviewDecisionAlert from '@/components/CustomerApp/UserProjects/OverviewDecisionAlert.vue';
import OverviewDecisionLogDialog from '@/components/CustomerApp/UserProjects/OverviewDecisionLogDialog.vue';
import OverviewMilestoneTimeline from '@/components/CustomerApp/UserProjects/OverviewMilestoneTimeline.vue';
import OverviewMilestoneDrawer from '@/components/CustomerApp/UserProjects/OverviewMilestoneDrawer.vue';
import OverviewMyCommitmentsPanel from '@/components/CustomerApp/UserProjects/OverviewMyCommitmentsPanel.vue';
import OverviewConstraintsPanel from '@/components/CustomerApp/UserProjects/OverviewConstraintsPanel.vue';
import OverviewPpcTrendChart from '@/components/CustomerApp/UserProjects/OverviewPpcTrendChart.vue';

export default {
  name: 'UserProjectsOverviewView',
  components: {
    Message,
    OverviewHeroHeader,
    OverviewKpiStrip,
    OverviewDecisionAlert,
    OverviewDecisionLogDialog,
    OverviewMilestoneTimeline,
    OverviewMilestoneDrawer,
    OverviewMyCommitmentsPanel,
    OverviewConstraintsPanel,
    OverviewPpcTrendChart,
  },
  data() {
    return {
      decisionLogVisible: false,
      openCreateOnShow: false,
      milestoneDrawerVisible: false,
      selectedMilestone: null,
    };
  },
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
      loading: 'overviewLoading',
      error: 'overviewError',
      isEmpty: 'overviewEmpty',
    }),
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      hasProjectContext: 'hasUserProjectContext',
      userProjectContextReady: 'userProjectContextReady',
    }),
    fatalError() {
      return Boolean(this.error) && this.isEmpty;
    },
  },
  watch: {
    selectedProjectId() {
      this.refresh();
    },
    userProjectContextReady(isReady) {
      if (isReady) {
        this.refresh();
      }
    },
  },
  async mounted() {
    if (this.userProjectContextReady) {
      await this.refresh();
    }
    if (this.$route.query.decisions === '1' && this.hasProjectContext) {
      this.openDecisionLog(true);
      this.$router.replace({ path: this.$route.path, query: {} });
    }
  },
  methods: {
    ...mapActions('pmCommitment', ['fetchUserProjectsOverview']),
    ...mapActions('pmPpc', ['fetchPpc']),
    ...mapActions('pmConstraint', ['fetchConstraintsSummary']),
    async refresh() {
      if (!this.userProjectContextReady) return;
      try {
        await Promise.all([
          this.fetchUserProjectsOverview(),
          this.fetchPpc(this.selectedProjectId),
          this.fetchConstraintsSummary(),
        ]);
      } catch {
        // handled in store
      }
    },
    openDecisionLog(openCreate = false) {
      this.openCreateOnShow = openCreate;
      this.decisionLogVisible = true;
    },
    openMilestoneDrawer(milestone) {
      this.selectedMilestone = milestone;
      this.milestoneDrawerVisible = true;
    },
  },
};
</script>

<style scoped>
.overview-page__empty {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}
</style>
